import { Checkbox, Grid, Skeleton } from '@mui/material';
import { HSpacer, RoutalPalette, RoutalSearchInput, RoutalTooltip } from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useEffect, useState } from 'react';

import moment from 'moment';
import AlertIcon from '../../../../assets/img/alert-icon.svg';
import DialogInfo from '../../../../assets/svg/DialogInfo';
import { Vehicle, WeekDay } from '../../../../domain/vehicle/Vehicle';
import { useSearchVehiclesEnabled } from '../../../../hooks/vehicle/useSearchVehiclesEnabled';
import Text from '../../../../react-components/Text';
import { AlignmentTypes, DataTable } from '../../../../react-components/data-table/DataTable';
import { VehicleAvatar } from '../../../shared/VehicleAvatar';

type VehiclesSelectionPlanCreationProps = {
  selectedVehicles: Vehicle[];
  setSelectedVehicles: (event?: Vehicle[]) => void;
  showWarning?: boolean;
  executionDate?: Date;
  setIsVehiclesLoading?: (isLoading: boolean) => void;
};

export const VehiclesSelectionPlanCreation = ({
  selectedVehicles,
  setSelectedVehicles,
  showWarning,
  executionDate,
  setIsVehiclesLoading,
}: VehiclesSelectionPlanCreationProps) => {
  const [filterText, setFilterText] = useState<string | undefined>(undefined);
  const [vehicleOffset, setVehicleOffset] = useState<number>(0);
  const [vehicleLimit, setVehicleLimit] = useState<number>(100);
  const [maxLimitVehicles, setMaxLimitVehicles] = useState<number>(0);

  const { isLoading: searchingVehicles, data: paginatedVehicles } = useSearchVehiclesEnabled({
    offset: vehicleOffset,
    limit: vehicleLimit,
    searchText: filterText,
  });

  useEffect(() => {
    if (!searchingVehicles) setIsVehiclesLoading && setIsVehiclesLoading(searchingVehicles);
  }, [searchingVehicles, setIsVehiclesLoading]);

  const day = () => {
    const local = moment.locale();
    const day = moment(executionDate).format(`dddd`);
    moment.locale(local);
    return day;
  };

  useEffect(() => {
    const max =
      paginatedVehicles?.total && maxLimitVehicles < paginatedVehicles.total
        ? paginatedVehicles.total
        : maxLimitVehicles;
    setMaxLimitVehicles(max);
  }, [paginatedVehicles]);

  const { docs, limit, page, pages } = paginatedVehicles || {};

  useEffect(() => {
    setSelectedVehicles(
      docs?.filter(
        (vehicle) => vehicle.default_working_days && vehicle.default_working_days.includes(day() as WeekDay)
      ) ?? []
    );
  }, [executionDate, docs]);

  return (
    <div style={{ height: `462px`, width: `450px` }}>
      <Grid container direction="column" style={{ height: `100%`, width: `100%` }} wrap="nowrap">
        <Grid container direction="row" alignItems="center" justifyContent="space-between" wrap="nowrap">
          <Grid
            item
            container
            alignItems="center"
            style={{
              minHeight: `46px`,
            }}
          >
            {searchingVehicles ? (
              <Skeleton variant="rectangular" animation="pulse" height={20} width={220} />
            ) : (
              <>
                <Text
                  variant="body"
                  intlId={`${
                    selectedVehicles.length > 0 ? `vehicles.header.selected_info` : `vehicles.header.num_rows`
                  }`}
                  intlValues={{
                    num: selectedVehicles.length,
                    total: maxLimitVehicles ?? ``,
                  }}
                />
                {showWarning && (
                  <Grid style={{ display: `flex`, flexWrap: `nowrap` }}>
                    <img src={AlertIcon} alt="" draggable="false" style={{ width: `14px`, marginRight: `4px` }} />
                    <Text
                      variant="body"
                      intlId={`planner.create_plan.vehicles_limit_exceeded`}
                      style={{ color: RoutalPalette.error.medium }}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>

          <Grid container style={{ height: `100%`, width: `100%` }} justifyContent="flex-end">
            <Grid item>
              <Grid container direction="column" justifyContent="center" style={{ height: `100%` }}>
                <RoutalSearchInput
                  inputContainerStyles={{
                    width: `240px`,
                  }}
                  onChange={_.debounce((text: any) => {
                    setFilterText(text);
                  }, 300)}
                  searchLive
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <DataTable
          loading={searchingVehicles}
          styledTableRowStyles={{
            selectedBackgroundColor: `transparent`,
          }}
          docs={(docs ?? [])
            .filter((vehicle) => vehicle.enabled)
            .map((vehicle) => ({
              doc: vehicle,
              checkboxIcon: {
                element: (
                  <Checkbox
                    style={{
                      height: `24px`,
                      width: `24px`,
                      opacity: !selectedVehicles.some((v) => v.id === vehicle.id) ? 0.3 : 1,
                    }}
                    checked={false}
                  />
                ),
                end: true,
                showAlways: true,
              },
              disabled: !selectedVehicles.some((v) => v.id === vehicle.id),
            }))}
          maxHeight="540px"
          total={maxLimitVehicles ?? 0}
          limit={limit}
          page={page}
          pages={pages}
          showCheckboxColumnRight={true}
          selectedIds={selectedVehicles.map((vehicle) => vehicle.id)}
          noFooter={(pages ?? 0) <= 1}
          emptyMessage={<Text intlId="no_results" />}
          onChangePage={({ offset, limit }) => {
            setVehicleOffset(offset);
            setVehicleLimit(limit);
          }}
          onMultipleSelection={({ selectedIds, fromGlobalCheckbox }) => {
            if (fromGlobalCheckbox) {
              if (selectedVehicles.length > 0) {
                setSelectedVehicles([]);
              } else {
                setSelectedVehicles(docs ?? []);
              }
            } else {
              const vehicles = docs?.filter((client) => (selectedIds ?? []).includes(client.id)) ?? [];
              vehicles.push(
                ...selectedVehicles.filter(
                  (selectedVehicle) => !(docs ?? []).map((docVehicle) => docVehicle.id).includes(selectedVehicle.id)
                )
              );
              setSelectedVehicles(_.uniqBy(vehicles, `id`));
            }
          }}
          onRowOneClick={(vehicle: { id: string }) => {
            const selectedVehicle = docs?.find((vehicleRow) => vehicleRow.id === vehicle.id);
            if (!selectedVehicle) return;
            if (selectedVehicles.some((v) => v.id === selectedVehicle.id)) {
              setSelectedVehicles(selectedVehicles.filter((v) => v.id !== selectedVehicle.id));
            } else {
              setSelectedVehicles(_.uniqBy([...selectedVehicles, selectedVehicle], `id`));
            }
          }}
          columns={[
            {
              accessorName: `label`,
              name: <Text variant="label" intlId="vehicles.data.details.label" />,
              width: `180px`,
              formatter: ({ doc }: { doc: Vehicle }) => (
                <Grid container direction="row" style={{ height: `30px` }} alignItems="center">
                  <Grid item alignItems="center" display={`flex`}>
                    <VehicleAvatar
                      vehicleId={doc.id}
                      vehicleColor={doc.color?.value}
                      additionalStyles={{ height: `20px` }}
                    />
                  </Grid>

                  <HSpacer small />

                  <Grid item>
                    {doc.label && doc.label !== `` ? (
                      <Text variant="body">
                        {doc.label.length > 18 ? `${doc.label.substring(0, 18)}...` : `${doc.label}`}
                      </Text>
                    ) : (
                      <Text variant="body" style={{ color: RoutalPalette.secondary00 }} intlId="no_name" />
                    )}
                  </Grid>
                </Grid>
              ),
            },
            {
              accessorName: `default_working_days`,
              align: `right` as AlignmentTypes,
              // name: <Text variant="label" intlId="vehicles.data.details.default_working_days" />,
              width: `50px`,
              formatter: ({ value: default_working_days, doc }: { value: string[]; doc: Vehicle }) => {
                if (
                  default_working_days &&
                  default_working_days.length > 0 &&
                  !default_working_days.includes(day() as WeekDay) &&
                  selectedVehicles.some((vehicle) => vehicle.id === doc.id)
                ) {
                  return (
                    <RoutalTooltip title={`planner.create_plan.vehicle_not_working`}>
                      <Grid style={{ display: `flex`, justifyContent: `center` }}>
                        <DialogInfo style={{ color: RoutalPalette.secondary40, height: `18px`, width: `18px` }} />
                      </Grid>
                    </RoutalTooltip>
                  );
                }
                return null;
              },
            },
          ]}
        />
      </Grid>
    </div>
  );
};
